/**
 * Enterprise API - Audit Trail & Knowledge Curation
 * Unwraps createSuccessResponse envelope: { success, data: { Records }, meta: { Total_Records, ... } }
 */

import { apiFetch } from './config';
import type { AuditLogEntry, CuratedKnowledge, PaginatedResponse } from '../types';

/** Unwrap paginated response from standard Records/meta format */
function unwrapPaginated<T>(response: any): { success: boolean; data?: PaginatedResponse<T>; error?: string } {
  if (!response.success) return { success: false, error: response.error };
  const body = response.data;
  const inner = body?.data || body;
  const meta = body?.meta || {};
  return {
    success: true,
    data: {
      records: inner?.Records || inner?.records || [],
      total: meta?.Total_Records ?? inner?.total ?? 0,
      page: meta?.Page ?? inner?.page ?? 1,
      pageSize: meta?.Page_Size ?? inner?.pageSize ?? 20,
    },
  };
}

/** Unwrap single-record response from standard Records array format */
function unwrapSingle<T>(response: any): { success: boolean; data?: T; error?: string } {
  if (!response.success) return { success: false, error: response.error };
  const body = response.data;
  const inner = body?.data || body;
  const record = inner?.Records?.[0] || inner;
  return { success: true, data: record as T };
}

export interface ControlTowerProject {
  project_id: string;
  project_name: string;
  repo_connected: boolean;
  sessions_30d: number;
  standards_used: number;
  last_session: string | null;
  status: 'active' | 'inactive';
}

export interface ControlTowerRisk {
  severity: 'high' | 'medium' | 'low';
  category: string;
  message: string;
}

export interface ControlTowerData {
  company_name: string;
  health_score: number | null;
  health_score_status?: 'available' | 'unavailable' | 'insufficient_evidence';
  unavailable_sources?: string[];
  team: {
    total: number;
    active: number;
    stale: number;
    very_stale: number;
    no_sessions: number;
  };
  projects: ControlTowerProject[];
  standards_coverage: {
    total_injections: number;
    unique_standards: number;
    sessions_with_standards: number;
  };
  governance: {
    audit_events_30d: number;
    knowledge_items: number;
    published_knowledge: number;
  };
  risks: ControlTowerRisk[];
  period_days: number;
  period_start: string;
}

export const enterpriseApi = {
  /**
   * Get audit log entries
   */
  getAuditLog: async (params: {
    page?: number;
    pageSize?: number;
    action?: string;
    resourceType?: string;
    startDate?: string;
    endDate?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.pageSize) queryParams.set('pageSize', params.pageSize.toString());
    if (params.action) queryParams.set('action', params.action);
    if (params.resourceType) queryParams.set('resourceType', params.resourceType);
    if (params.startDate) queryParams.set('startDate', params.startDate);
    if (params.endDate) queryParams.set('endDate', params.endDate);

    const response = await apiFetch<any>(
      `/api/enterprise/audit?${queryParams.toString()}`
    );
    return unwrapPaginated<AuditLogEntry>(response);
  },

  /**
   * Get curated knowledge items
   */
  getKnowledge: async (params: {
    page?: number;
    pageSize?: number;
    status?: 'draft' | 'published' | 'archived';
    category?: string;
  }) => {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.set('page', params.page.toString());
    if (params.pageSize) queryParams.set('pageSize', params.pageSize.toString());
    if (params.status) queryParams.set('status', params.status);
    if (params.category) queryParams.set('category', params.category);

    const response = await apiFetch<any>(
      `/api/enterprise/knowledge?${queryParams.toString()}`
    );
    return unwrapPaginated<CuratedKnowledge>(response);
  },

  /**
   * Create curated knowledge item
   */
  createKnowledge: async (data: {
    title: string;
    content: string;
    category?: string;
    tags?: string[];
    invariantId?: number;
  }) => {
    const response = await apiFetch<any>('/api/enterprise/knowledge', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return unwrapSingle<CuratedKnowledge>(response);
  },

  /**
   * Update curated knowledge item
   */
  updateKnowledge: async (
    knowledgeId: number,
    data: {
      title?: string;
      content?: string;
      category?: string;
      tags?: string[];
      status?: 'draft' | 'published' | 'archived';
    }
  ) => {
    const response = await apiFetch<any>('/api/enterprise/knowledge', {
      method: 'PUT',
      body: JSON.stringify({ knowledge_id: knowledgeId, ...data }),
    });
    return unwrapSingle<CuratedKnowledge>(response);
  },

  /**
   * Delete curated knowledge item
   */
  deleteKnowledge: async (knowledgeId: number) => {
    const response = await apiFetch<any>(
      `/api/enterprise/knowledge?knowledge_id=${knowledgeId}`,
      { method: 'DELETE' }
    );
    if (!response.success) return { success: false, error: response.error };
    const body = response.data;
    const message = body?.message || body?.data?.message || 'Deleted';
    return { success: true, data: { message } as { message: string } };
  },

  /**
   * Get knowledge categories
   */
  getCategories: async () => {
    const response = await apiFetch<any>(
      '/api/enterprise/knowledge/categories'
    );
    if (!response.success) return { success: false, error: response.error };
    const body = response.data;
    const inner = body?.data || body;
    const records = inner?.Records || inner?.records || inner || [];
    return { success: true, data: records as { category_id: number; name: string; description?: string }[] };
  },

  /**
   * Get contributor stats
   */
  getContributorStats: async (params: { period?: string }) => {
    const queryParams = params.period ? `?period=${params.period}` : '';
    const response = await apiFetch<any>(`/api/enterprise/contributors${queryParams}`);
    if (!response.success) return { success: false, error: response.error };
    const body = response.data;
    const inner = body?.data || body;
    const records = inner?.Records || inner?.records || inner || [];
    return { success: true, data: records as {
      contributor_email: string;
      discoveries: number;
      refinements: number;
      verifications: number;
      curations: number;
      total_weight: number;
    }[] };
  },

  // ============================================================
  // Control Tower
  // ============================================================

  /**
   * Get Control Tower org health data
   */
  getControlTower: async () => {
    const response = await apiFetch<any>('/api/enterprise/control-tower');
    if (!response.success) return { success: false, error: response.error };
    const body = response.data;
    const data = body?.data || body;
    return { success: true, data: data as ControlTowerData };
  },

  // ============================================================
  // Enterprise Onboarding
  // ============================================================

  /**
   * Get enterprise onboarding completion status
   */
  getOnboardingStatus: async () => {
    const response = await apiFetch<any>('/api/enterprise/onboarding/status');
    return unwrapSingle<{
      company_id?: string;
      step_completed: number;
      org_configured: boolean;
      invites_sent: number;
      standards_configured: boolean;
      repo_connected: boolean;
      completed: boolean;
      not_enterprise?: boolean;
    }>(response);
  },

  /**
   * Save organization profile during onboarding
   */
  setupOrganization: async (data: {
    company_name: string;
    domain: string;
    industry?: string;
    company_size?: string;
  }) => {
    const response = await apiFetch<any>('/api/enterprise/onboarding/setup', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return unwrapSingle<{ company_id: string; company_name: string; step_completed: number }>(response);
  },

  /**
   * Send team invitations during onboarding
   */
  inviteTeamMembers: async (invitations: { email: string; role: string }[]) => {
    const response = await apiFetch<any>('/api/enterprise/onboarding/invite', {
      method: 'POST',
      body: JSON.stringify({ invitations }),
    });
    if (!response.success) return { success: false, error: response.error };
    const body = response.data;
    const inner = body?.data || body;
    return {
      success: true,
      data: {
        sent: inner?.sent ?? 0,
        invitations: inner?.Records || [],
      },
    };
  },

  /**
   * Mark enterprise onboarding as completed
   */
  completeOnboarding: async (enabledCategories?: Record<string, boolean>) => {
    const response = await apiFetch<any>('/api/enterprise/onboarding/complete', {
      method: 'POST',
      body: JSON.stringify({ enabled_categories: enabledCategories }),
    });
    return unwrapSingle<{ company_id: string; completed: boolean }>(response);
  },
};
